// src/utils/vehicleVerification.js
// Vehicle verification utilities for license plate recognition and PIN verification

/**
 * Normalize license plate string
 * @param {string} plate - Raw license plate input
 * @returns {string} - Normalized plate (uppercase, no spaces/hyphens)
 */
export function normalizePlate(plate) {
  if (!plate || typeof plate !== 'string') return '';
  return plate.trim().toUpperCase().replace(/[\s-]/g, '');
}

/**
 * Check if a license plate is recognized (MOCK - UI ONLY)
 * TODO: Replace with API call to check if plate is registered for fleet/special rates
 * 
 * @param {string} plate - License plate to check
 * @returns {boolean} - True if plate is recognized
 */
export function isPlateRecognized(plate) {
  const normalized = normalizePlate(plate);
  if (!normalized) return false;
  
  // TODO: Replace with API call: GET /api/vehicles/check-plate?plate={normalized}
  // Expected response: { recognized: boolean, requiresPin: boolean }
  
  // Mock: Hardcoded list of recognized plates
  const RECOGNIZED_PLATES = ['FLEET123', 'VIP001', 'B1234'];
  return RECOGNIZED_PLATES.includes(normalized);
}

/**
 * Get mock PIN for a plate (MOCK - UI ONLY)
 * TODO: Replace with backend PIN verification API
 * 
 * @param {string} plate - License plate
 * @returns {string|null} - Mock PIN or null if no PIN defined
 */
export function getMockPinForPlate(plate) {
  const normalized = normalizePlate(plate);
  
  // TODO: Remove this function entirely - PIN verification should happen on backend
  // Expected API: POST /api/vehicles/verify-pin { plate, pin }
  // Expected response: { valid: boolean, authToken?: string }
  
  // Mock PIN mappings
  const PIN_MAP = {
    'FLEET123': '1234',
    'VIP001': '0000',
    'B1234': '4321',
  };
  
  return PIN_MAP[normalized] || null;
}

/**
 * Verify PIN for a plate (MOCK - UI ONLY)
 * @param {string} plate - License plate
 * @param {string} pin - 4-digit PIN
 * @returns {boolean} - True if PIN is correct
 */
export function verifyPin(plate, pin) {
  const mockPin = getMockPinForPlate(plate);
  return mockPin === pin;
}

/**
 * Get lockout info from localStorage
 * @param {string} plate - Normalized license plate
 * @returns {object|null} - { lockedUntil: timestamp, attempts: number } or null
 */
export function getLockoutInfo(plate) {
  try {
    const key = `pin_lockout_${plate}`;
    const data = localStorage.getItem(key);
    if (!data) return null;
    
    const lockout = JSON.parse(data);
    
    // Check if lockout has expired
    if (lockout.lockedUntil && Date.now() > lockout.lockedUntil) {
      // Lockout expired, clear it
      localStorage.removeItem(key);
      return null;
    }
    
    return lockout;
  } catch (error) {
    console.error('[vehicleVerification] Failed to get lockout info:', error);
    return null;
  }
}

/**
 * Set lockout for a plate
 * @param {string} plate - Normalized license plate
 * @param {number} durationMs - Lockout duration in milliseconds (default 10 minutes)
 */
export function setLockout(plate, durationMs = 10 * 60 * 1000) {
  try {
    const key = `pin_lockout_${plate}`;
    const lockout = {
      lockedUntil: Date.now() + durationMs,
      attempts: 3, // Max attempts reached
      lockedAt: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(lockout));
  } catch (error) {
    console.error('[vehicleVerification] Failed to set lockout:', error);
  }
}

/**
 * Clear lockout for a plate
 * @param {string} plate - Normalized license plate
 */
export function clearLockout(plate) {
  try {
    const key = `pin_lockout_${plate}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('[vehicleVerification] Failed to clear lockout:', error);
  }
}

/**
 * Get remaining lockout time in seconds
 * @param {string} plate - Normalized license plate
 * @returns {number} - Remaining seconds or 0 if not locked
 */
export function getRemainingLockoutTime(plate) {
  const lockout = getLockoutInfo(plate);
  if (!lockout || !lockout.lockedUntil) return 0;
  
  const remaining = Math.max(0, lockout.lockedUntil - Date.now());
  return Math.ceil(remaining / 1000);
}

/**
 * Format seconds to MM:SS
 * @param {number} seconds - Total seconds
 * @returns {string} - Formatted time string (MM:SS)
 */
export function formatLockoutTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
